import type {
  VisualModel, VisualColumn, CriteriaNode, CriteriaRule, DataSourceMode,
} from './types'
import { isCriteriaGroup } from './types'
import { VISUAL_CATALOG, type VisualCatalogEntry } from './catalog'
import { parseSourceSql } from './sqlParser'
import { dropMovedColumnCopies } from '@twasta/visual-engine'

export function blankColumn(colId: number): VisualColumn {
  return {
    COLID: colId,
    NAME: `COLUMN_${colId}`,
    DBTABLE: '',
    DBNAME: '',
    tableName: '',
    tableDisplayName: '',
    content: `Column ${colId}`,
    name: `Column ${colId}`,
    descr: '',
    type: 'string',
    JAVATYPE: 'java.lang.String',
    COLTYPE: 'CHAR',
    NATIVETYPE: 'AN',
    DBSIZE: 100,
    EXPRESSIONTYPE: 'C',
    DEFAULTFUNCTION: '',
    FUNCTION: '',
    FEILD_TYPE: 'TEXTBOX',
    ALIGNMENT: 1,
    alignment: '',
    WIDTH: 100,
    FONT: 'TIMES NEW ROMAN',
    FONTSIZE: 12,
    FGCOLOR: '#000000',
    BGCOLOR: '',
    BOLD: 0,
    ITALIC: 0,
    UNDERLINE: 0,
    HIDDEN: '',
    KEY: false,
    CAPS: false,
    ADV_FORMAT: '',
    expression: '',
    value: '',
    groupName: 'Columns',
    StandardName: 'columns',
    checked: true,
  }
}

export function blankCriteriaRule(n: number): CriteriaRule {
  return {
    id: `Criteria_${n}`,
    field: '',
    DBNAME: '',
    tableName: '',
    COLTYPE: 'string',
    currentFieldType: 'string',
    operator: '=',
    queryOption: 'P',
    prevOption: 'P',
    customValue: 'FixedValue',
    promptLabel: '',
    value: '',
    chipValue: [],
  }
}

// Catalog lookup that tolerates LLM-emitted variations: case-insensitive,
// whitespace-tolerant, and falls back via the visual ID when the name is
// unrecognised. Returns Tabular (catalog[0]) only as a last resort.
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']

/**
 * A DATE criterion's value as the `YYYY-MM-DD` a date input accepts.
 *
 * `<input type="date">` renders anything else EMPTY, and an imported visual
 * carries two such shapes: the app server datepicker's value (the picked day's
 * LOCAL midnight as a UTC stamp — `2024-07-16T18:30:00.000Z` = 17 July in IST;
 * the picked day is the nearest UTC midnight) and a freehand placeholder's
 * default (`24-aug-2019`, `01/01/2017`, day first). Anything else is returned
 * unchanged. Mirrors the backend's `bi_sql.placeholders.criteria_date_iso`.
 */
export function criteriaDateIso(value: string): string {
  const text = (value || '').trim()
  const iso = (y: number, mo: number, d: number): string | null => {
    const t = new Date(Date.UTC(y, mo - 1, d))
    return t.getUTCFullYear() === y && t.getUTCMonth() === mo - 1 && t.getUTCDate() === d
      ? t.toISOString().slice(0, 10) : null
  }
  let m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?Z$/.exec(text)
  if (m) {
    const [y, mo, d, hh, mi, ss] = m.slice(1).map((g) => Number(g || 0))
    return new Date(Date.UTC(y, mo - 1, d, hh, mi, ss) + 12 * 3600 * 1000).toISOString().slice(0, 10)
  }
  m = /^(\d{1,2})[-/ ]([A-Za-z]{3})[-/ ](\d{2}|\d{4})$/.exec(text)
  if (m && MONTHS.includes(m[2].toLowerCase())) {
    let y = Number(m[3])
    if (y < 100) y += y < 70 ? 2000 : 1900
    return iso(y, MONTHS.indexOf(m[2].toLowerCase()) + 1, Number(m[1])) ?? value
  }
  m = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(text)
  if (m) return iso(Number(m[3]), Number(m[2]), Number(m[1])) ?? value
  return value
}

export function findCatalogEntry(
  visualName?: string,
  visualId?: string
): VisualCatalogEntry {
  const norm = (s: string | undefined) =>
    (s || '').toLowerCase().replace(/[\s_-]+/g, '')

  const target = norm(visualName)
  if (target) {
    const byName = VISUAL_CATALOG.find((c) => norm(c.VisualName) === target)
    if (byName) return byName
  }
  const targetId = norm(visualId)
  if (targetId) {
    const byId = VISUAL_CATALOG.find((c) => norm(c.ID) === targetId)
    if (byId) return byId
  }
  return VISUAL_CATALOG[0]
}

// Detect the data-source mode for a previously-saved visual JSON.
// Trust an explicit DATA_SOURCE_MODE if present; otherwise guess from
// content (sqlmodel mode wins when there are structured columns).
function detectMode(
  raw: Record<string, unknown>,
  hasStructuredCols: boolean,
  sourceSql: string
): DataSourceMode {
  const explicit = raw.DATA_SOURCE_MODE as string | undefined
  if (explicit === 'sql' || explicit === 'sqlmodel') return explicit
  if (hasStructuredCols) return 'sqlmodel'
  if (sourceSql && sourceSql.trim()) return 'sql'
  return 'sqlmodel'
}

function blankFromCatalog(entry: VisualCatalogEntry): VisualModel {
  return {
    DATA_SOURCE_MODE: 'sqlmodel',
    SQLMODEL: {
      SQLModel: { COLUMNS: [{ COLUMN: [] }] },
      CRITERIA: { query: { rules: [], condition: 'and' } },
      VISUAL_NAME: entry.VisualName,
      SCHEMA_NAME: '',
      BUSINESS_AREA: '',
      SCHEMA_DESCR: '',
      SCHEMA_TYPE: 'S',
      OUTPUT_TYPE: 'JSON',
      DATABASE_TYPE: '1',
      DATABASE_DETAIL: { DATABASE_NAME: 'Oracle', TYPE: 1 },
    },
    LAYOUT_DATA: {
      visualLayout: {
        ID: entry.ID,
        VisualName: entry.VisualName,
        VisualType: entry.VisualType,
        VisualIcon: entry.VisualIcon,
        DisplayOrder: entry.DisplayOrder,
        className: entry.className,
        linkOptions: '',
        OutputType: entry.OutputType,
        options: entry.options.map((o) => ({ ...o })),
        ColumnGroups: entry.ColumnGroups.map((gs, i) => ({
          GroupID: i + 1,
          GroupName: gs.GroupName,
          GroupDescription: gs.GroupDescription,
          GroupIcon: gs.GroupIcon,
          AllowedColumnTypes: gs.AllowedColumnTypes,
          MinColumns: gs.MinColumns,
          MaxColumns: gs.MaxColumns,
          StandardName: gs.StandardName,
          COLUMNS: [],
        })),
      },
      layoutdata: {},
    },
    SOURCE_SQL: '',
  }
}

export function blankVisualModel(objName: string): VisualModel {
  const tabular = VISUAL_CATALOG[0]
  const m = blankFromCatalog(tabular)
  m.SQLMODEL.SCHEMA_NAME = (objName || '').toUpperCase().replace(/[^A-Z0-9_]/g, '_') || 'VISUAL'
  m.SQLMODEL.SCHEMA_DESCR = (objName || '').replace(/_/g, ' ')
  return m
}

// Make a loaded JSON safe to drive the editor. Fills in missing top-level
// keys, reattaches the catalog entry's groups when the visual matches,
// guarantees the column array exists, etc. Never throws — falls back to a
// blank model if the input is unusable.
// A well that holds the same column twice, once ticked and once unticked, is
// what the app server's Manage Visual saves when it re-lays out a visual the
// platform deployed: it adds its own (ticked) copy and keeps the old one
// unticked beside it. Only the unticked copy of a column that is ALSO present
// ticked is dropped — two ticked copies (SUM and COUNT of one measure) are two
// real entries, and an unticked column on its own is left alone.
function dropStaleCopies(cols: VisualColumn[]): VisualColumn[] {
  const key = (c: VisualColumn) => String(c?.NAME || '').trim().toLowerCase()
  const ticked = new Set(cols.filter((c) => c?.checked !== false).map(key))
  return cols.filter((c) => c?.checked !== false || !ticked.has(key(c)))
}

export function sanitizeVisualModel(raw: unknown, objName: string): VisualModel {
  if (!raw || typeof raw !== 'object') return blankVisualModel(objName)
  const r = raw as Record<string, unknown>

  const sqlmodel = (r.SQLMODEL as Record<string, unknown>) || {}
  const layoutData = (r.LAYOUT_DATA as Record<string, unknown>) || {}
  const visualLayout = (layoutData.visualLayout as Record<string, unknown>) || {}
  const visualName =
    (sqlmodel.VISUAL_NAME as string) ||
    (visualLayout.VisualName as string)
  const visualIdRaw = visualLayout.ID as string | undefined

  const entry = findCatalogEntry(visualName, visualIdRaw)

  const sqlInner = (sqlmodel.SQLModel as Record<string, unknown>) || {}
  const colsRaw = sqlInner.COLUMNS as unknown
  // Accept three shapes the LLM may emit:
  //   (a) [{ COLUMN: [...cols] }]   ← canonical runtime shape
  //   (b) [...cols]                 ← collapsed form, just the column array
  //   (c) { COLUMN: [...cols] }     ← single object instead of an array
  let columns: VisualColumn[] = []
  if (Array.isArray(colsRaw) && colsRaw.length > 0) {
    const first = colsRaw[0]
    if (first && typeof first === 'object' && Array.isArray((first as Record<string, unknown>).COLUMN)) {
      // shape (a)
      columns = (first as Record<string, unknown>).COLUMN as VisualColumn[]
    } else {
      // shape (b)
      columns = colsRaw as VisualColumn[]
    }
  } else if (colsRaw && typeof colsRaw === 'object' && Array.isArray((colsRaw as Record<string, unknown>).COLUMN)) {
    columns = (colsRaw as Record<string, unknown>).COLUMN as VisualColumn[]
  }
  // The app server's master list carries one copy per PLACEMENT (its
  // generateSQLSeq() pushes every group's columns), so a column placed in two
  // wells is listed twice — one entry per column, as syncMasterFromGroups
  // writes it on the next save.
  {
    const seen = new Set<string>()
    columns = columns.filter((c) => {
      if (typeof c?.COLID !== 'number') return true
      const k = `${c.COLID}|${String(c.NAME || '').toUpperCase()}`
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })
  }
  // Defensive: ensure each column has a usable COLID
  let nextColId = 2
  columns = columns.map((c) => {
    const out = { ...c }
    if (typeof out.COLID !== 'number') out.COLID = nextColId++
    if (typeof out.COLID === 'number' && out.COLID >= nextColId) nextColId = out.COLID + 1
    return out
  })

  const criteria = (sqlmodel.CRITERIA as Record<string, unknown>) || {}
  const query = (criteria.query as Record<string, unknown>) || {}
  const rules = Array.isArray(query.rules) ? (query.rules as CriteriaNode[]) : []
  const condition = (query.condition as 'and' | 'or') || 'and'
  // Guarantee every LEAF criterion (at any group nesting depth) a UNIQUE id —
  // the designer addresses rules by position but keys React lists by id, and
  // older files (pre-duplicate-drop) could carry collisions. GROUPS get a
  // unique designer-only id too (collapse state keys); engines ignore it.
  {
    const seen = new Set<string>()
    let n = 1
    let g = 1
    const fix = (r: Record<string, unknown>, prefix: string) => {
      let id = typeof r.id === 'string' ? r.id : ''
      while (!id || seen.has(id)) {
        id = prefix === 'Group' ? `Group_${g++}` : `Criteria_${n++}`
      }
      seen.add(id)
      r.id = id
    }
    const walk = (nodes: CriteriaNode[]) => {
      for (const node of nodes) {
        if (isCriteriaGroup(node)) {
          fix(node as Record<string, unknown>, 'Group')
          walk((node.rules || []) as CriteriaNode[])
        } else fix(node as Record<string, unknown>, 'Criteria')
      }
    }
    walk(rules)
  }

  // Reconcile ColumnGroups against the catalog: keep group structure from
  // the catalog (so the UI always shows the right slots), but pull the
  // column lists from whatever the saved file had under each StandardName.
  // Matched case-insensitively: the app server's Manage Visual saves
  // "rows"/"columns"/"values" where the catalog says "Rows"/…, so an exact
  // match opened every reverse-imported visual with its wells empty. The
  // renderers already lowercase it (visual-engine dataUtils).
  // An app-server import can list a MOVED column in the group it left too.
  const savedGroups = dropMovedColumnCopies(
    (visualLayout.ColumnGroups as Record<string, unknown>[]) || [],
  )
  const savedByStd = new Map<string, VisualColumn[]>()
  for (const g of savedGroups) {
    const std = String(g?.StandardName || '').trim().toLowerCase()
    const cols = (g?.COLUMNS as VisualColumn[]) || []
    if (std) savedByStd.set(std, Array.isArray(cols) ? dropStaleCopies(cols) : [])
  }

  const groups = entry.ColumnGroups.map((gs, i) => ({
    GroupID: i + 1,
    GroupName: gs.GroupName,
    GroupDescription: gs.GroupDescription,
    GroupIcon: gs.GroupIcon,
    AllowedColumnTypes: gs.AllowedColumnTypes,
    MinColumns: gs.MinColumns,
    MaxColumns: gs.MaxColumns,
    StandardName: gs.StandardName,
    COLUMNS: savedByStd.get(gs.StandardName.toLowerCase()) || [],
  }))

  // Reconcile options against the catalog so the UI knows the LOV/heading,
  // but keep the user's saved defaultValue when present.
  const savedOptions = (visualLayout.options as Record<string, unknown>[]) || []
  const savedOptByName = new Map<string, string>()
  for (const o of savedOptions) {
    const name = (o?.optionName as string) || ''
    const val = (o?.defaultValue as string) ?? ''
    if (name) savedOptByName.set(name, val)
  }
  const options = entry.options.map((o) => ({
    ...o,
    defaultValue: savedOptByName.has(o.optionName)
      ? savedOptByName.get(o.optionName)!
      : o.defaultValue,
  }))
  // Migrate the legacy Card `sparkline` boolean (dropped from the catalog) onto
  // the new `microVisual` select so existing trend cards keep their sparkline.
  const legacySpark = (savedOptByName.get('sparkline') || '').toLowerCase()
  if (legacySpark === 'true' || legacySpark === 'on' || legacySpark === '1') {
    const micro = options.find((o) => o.optionName === 'microVisual')
    if (micro && (micro.defaultValue || 'auto') === 'auto') micro.defaultValue = 'sparkline'
  }

  const sourceSql = (r.SOURCE_SQL as string) || ''
  const mode = detectMode(r, columns.length > 0, sourceSql)

  // SQL mode: the SELECT is the column list, so drop anything the query no
  // longer returns. A visual that was built on a schema (or on an earlier
  // query) keeps its old placements otherwise — the Layout offers columns the
  // SELECT never mentions, and the preview draws them as a grid of nulls,
  // which reads as a wrong ANSWER rather than a stale layout.
  //
  // Only when the parse is DEFINITE: `SELECT *` names no columns (only the
  // database can expand it — that is what the sql-columns endpoint is for) and
  // a query that parses to nothing is mid-edit or unparseable. In both cases
  // the placements are left exactly as they were.
  // The mirror of the same rule: a schema visual carries no SOURCE_SQL. One
  // saved before the mode switch still does, and it is invisible in the
  // designer (the SQL editor only shows in SQL mode) right up until a deploy
  // hands it to an app server that prefers it to the schema.
  const effectiveSourceSql = mode === 'sql' ? sourceSql : ''
  const parsedSql = mode === 'sql' ? parseSourceSql(sourceSql) : null
  if (parsedSql && !parsedSql.hasStar && parsedSql.columns.length) {
    const aliases = new Set(parsedSql.columns.map((c) => c.alias.toUpperCase()))
    const returned = (c: VisualColumn) => aliases.has((c.NAME || '').toUpperCase())
    columns = columns.filter(returned)
    for (const g of groups) g.COLUMNS = (g.COLUMNS || []).filter(returned)
  }

  // Top-N keys: ORDER_BY [{column, direction}] + ROW_LIMIT (accepts the
  // engine's {value: n} form too). Rebuilding SQLMODEL from an allow-list
  // must not silently drop them — a saved Top-N would vanish on next save.
  const orderByRaw = sqlmodel.ORDER_BY
  const orderBy = Array.isArray(orderByRaw)
    ? (orderByRaw as { column?: string; direction?: string }[])
        .filter((e) => e && typeof e === 'object' && (e.column || '').trim())
        .map((e) => ({
          column: String(e.column).trim(),
          direction: String(e.direction || 'ASC').toUpperCase() === 'DESC' ? 'DESC' as const : 'ASC' as const,
        }))
    : []
  const calculations = (Array.isArray(sqlmodel.CALCULATIONS) ? sqlmodel.CALCULATIONS as Record<string, unknown>[] : [])
    .filter((c) => c && typeof c === 'object' && String(c.key || '').trim() && String(c.label || '').trim())
    .map((c) => ({
      key: String(c.key).trim(),
      label: String(c.label).trim(),
      formula: String(c.formula || ''),
      when: (c.when === 'row' ? 'row' : 'total') as 'total' | 'row',
    }))
  const rowLimitRaw = sqlmodel.ROW_LIMIT
  const rowLimitNum = parseInt(String(
    (rowLimitRaw && typeof rowLimitRaw === 'object'
      ? (rowLimitRaw as Record<string, unknown>).value
      : rowLimitRaw) ?? '',
  ), 10)

  return {
    DATA_SOURCE_MODE: mode,
    SQLMODEL: {
      SQLModel: { COLUMNS: [{ COLUMN: columns }] },
      CRITERIA: { query: { rules, condition } },
      ...(orderBy.length ? { ORDER_BY: orderBy } : {}),
      ...(Number.isFinite(rowLimitNum) && rowLimitNum > 0 ? { ROW_LIMIT: rowLimitNum } : {}),
      // Calculations are kept as authored (rebuilt from an allow-list, so a
      // key left out here would be erased by the next save).
      ...(calculations.length ? { CALCULATIONS: calculations } : {}),
      VISUAL_NAME: entry.VisualName,
      SCHEMA_NAME: (sqlmodel.SCHEMA_NAME as string) || (objName || '').toUpperCase(),
      BUSINESS_AREA: (sqlmodel.BUSINESS_AREA as string) || '',
      SCHEMA_DESCR: (sqlmodel.SCHEMA_DESCR as string) || objName,
      SCHEMA_TYPE: (sqlmodel.SCHEMA_TYPE as string) || 'S',
      OUTPUT_TYPE: (sqlmodel.OUTPUT_TYPE as string) || 'JSON',
      DATABASE_TYPE: (sqlmodel.DATABASE_TYPE as string) || '1',
      DATABASE_DETAIL:
        (sqlmodel.DATABASE_DETAIL as { DATABASE_NAME: string; TYPE: number }) ||
        { DATABASE_NAME: 'Oracle', TYPE: 1 },
    },
    LAYOUT_DATA: {
      visualLayout: {
        ID: entry.ID,
        VisualName: entry.VisualName,
        VisualType: entry.VisualType,
        VisualIcon: entry.VisualIcon,
        DisplayOrder: entry.DisplayOrder,
        className: entry.className,
        linkOptions: (visualLayout.linkOptions as string) || '',
        OutputType: entry.OutputType,
        options,
        ColumnGroups: groups,
        // Events on the timeline are kept as authored (rebuilt from an
        // allow-list, so a key left out here would be erased by the next save).
        ...(visualLayout.events && typeof visualLayout.events === 'object'
          && Array.isArray((visualLayout.events as { layers?: unknown }).layers)
          ? { events: visualLayout.events as import('@twasta/visual-engine').EventsBlock } : {}),
      },
      layoutdata: (layoutData.layoutdata as Record<string, unknown>) || {},
    },
    SOURCE_SQL: effectiveSourceSql,
  }
}
